/** Copyright (c) 2019 T.Rowe Price. All rights reserved. */

import { groupBy, orderBy, reduce } from 'lodash';
import moment from 'moment';
import { createSelector } from 'reselect';

import {
  formatAccountOwners,
  getAccountForDetails
} from 'state/account/AccountSelectors';
import { updateAccountFilterOptions } from 'services/Filters';
import { DATE_DATA_FORMAT } from 'services/Dates';
import { filterPendingTransactionsByAccount } from 'services/Transactions';

export const getHistoricalTransactionTable = (state) =>
  state.transactionsHistoryTable;
export const getPendingTransactions = (state) => state.pendingTransactions;
export const getPendingTransactionsTable = (state) =>
  state.transactionsPendingTable;
export const getTransactionsHistoryFilteringSelections = (state) =>
  state.transactionsHistoryFilteringSelections;
export const getRawHistoryTransactions = (state) =>
  state.rawTransactionsHistory;

export const getFilteringSelections = createSelector(
  getTransactionsHistoryFilteringSelections,
  (filteringSelections) => {
    return updateAccountFilterOptions(
      filteringSelections.includedAccounts,
    );
  }
);

const filterGroupByAccount = (account, groups, filter, limit = 5) => {
  const filteredGroup = {};
  Object.entries(groups).forEach(([date, transactions]) => {
    const filteredTransactions = filter(account, transactions);
    if (filteredTransactions.length !== 0) {
      filteredGroup[date] = filteredTransactions;
    }
  });
  const limitGroup = {};
  let count = 0;
  Object.entries(filteredGroup).forEach(([data, transactions]) => {
    if (count <= limit) {
      const limitedTransactions = transactions.slice(0, limit - count);
      count = count + limitedTransactions.length;
      limitGroup[data] = limitedTransactions;
    }
  });
  return limitGroup;
};

export const getGroupsForPendingTransactions = createSelector(
  getPendingTransactionsTable,
  (pendingTransactions) => {
    const sortedPendingTransactions = orderBy(
      pendingTransactions,
      ({ tradeDate }) => tradeDate,
      'desc'
    );
    const groups = groupBy(sortedPendingTransactions, (t) =>
      moment.utc(t.tradeDate).format(DATE_DATA_FORMAT)
    );
    const sortedGroups = reduce(
      groups,
      (acc, accounts, key) => {
        const sorted = orderBy(
          accounts,
          ({ typeInformation }) => typeInformation.confirmationNumber,
          'desc'
        );
        acc[key] = sorted;
        return acc;
      },
      {}
    );
    return sortedGroups;
  }
);

export const getSortedPendingTransactions = createSelector(
  getGroupsForPendingTransactions,
  (sortedGroups) =>
    Object.keys(sortedGroups)
      .sort((a, b) => moment.utc(b).diff(moment.utc(a)))
      .reduce((acc, current) => acc.concat(sortedGroups[current]), [])
);

export const getAccountPendingTransactions = createSelector(
  getAccountForDetails,
  getPendingTransactionsTable,
  (account, pendingTransactions) =>
    filterPendingTransactionsByAccount(account, pendingTransactions)
);

export const getAccountPendingTransactionsGroups = createSelector(
  getAccountForDetails,
  getGroupsForPendingTransactions,
  (account, pendingGroups) =>
    filterGroupByAccount(
      account,
      pendingGroups,
      filterPendingTransactionsByAccount
    )
);

const filterHistoryTransactionsByAccount = (
  account,
  transactions = [],
  limit = 5
) => {
  const { accountId, accountNumber, accountChecksum } = account;
  const accountWChecksum = `${accountNumber}-${accountChecksum}`;
  const filteredTransactions = transactions.filter(
    (transaction) => transaction.accountId === accountId
  );
  const transformedFilteredTransactions = filteredTransactions.map(
    (transaction) => {
      return {
        accountName: account.accountName,
        accountType: account.accountType,
        accountWChecksum,
        accountId: account.accountId,
        associatedWithShareClassConversion: transaction?.associatedWithShareClassConversion,
        costBasis: transaction.costBasis,
        netDollarAmount: transaction.amount,
        owners: formatAccountOwners(account),
        price: transaction.price,
        productType: account.productType,
        quantity: transaction.quantity,
        ticker: account.ticker,
        tradeDate: transaction.date,
        transactionDescription: transaction.detail,
        transactionType: transaction.type,
        isViewOnly: account.isViewOnly
      };
    }
  );
  return orderBy(
    transformedFilteredTransactions,
    ['tradeDate'],
    ['desc']
  ).slice(0, limit);
};

export const getGroupsForHistoryTransactions = createSelector(
  getHistoricalTransactionTable,
  (historyTransactions) =>
    groupBy(historyTransactions, (t) =>
      moment.utc(t.tradeDate).format(DATE_DATA_FORMAT)
    )
);

export const getAccountHistoryTransactions = createSelector(
  getAccountForDetails,
  getRawHistoryTransactions,
  (account, rawHistoryTransactions) =>
    filterHistoryTransactionsByAccount(account, rawHistoryTransactions)
);

export const getAccountHistoryTransactionsGroups = createSelector(
  getAccountHistoryTransactions,
  (historyTransactions) =>
    groupBy(historyTransactions, (t) =>
      moment.utc(t.tradeDate).format(DATE_DATA_FORMAT)
    )
);
